import {
  CheckCircle, ChevronRight, FileText 
} from 'lucide-react';
import {
  Card, CardContent 
} from '@/components/ui/card';
import { ResolutionRoom } from '@/features/receiving';
import type { Discrepancy } from '@/features/receiving';
import { DISCREPANCY_TYPE_LABELS } from '@/features/receiving/types/discrepancy';

function pluralize(count: number, singular: string, plural: string): string {
  return count === 1 ? singular : plural;
}

interface DiscrepanciesViewProps {
  readonly discrepancies: Discrepancy[];
  readonly isLoading: boolean;
  readonly selectedDiscrepancy: Discrepancy | null;
  readonly onSelectDiscrepancy: (d: Discrepancy | null) => void;
}

export function DiscrepanciesView({
  discrepancies,
  isLoading,
  selectedDiscrepancy,
  onSelectDiscrepancy,
}: DiscrepanciesViewProps) {
  const pendingCount = discrepancies.filter((d) => d.status === 'pending_receiver_confirmation').length;

  if (isLoading) {
    return (
      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="h-32 animate-pulse bg-slate-100 border-slate-200" />
        <Card className="h-32 animate-pulse bg-slate-100 border-slate-200" />
      </div>
    );
  }

  if (discrepancies.length === 0) {
    return (
      <Card className="border-slate-200">
        <CardContent className="flex flex-col items-center justify-center py-16">
          <div className="h-12 w-12 rounded-full bg-emerald-50 flex items-center justify-center mb-4">
            <CheckCircle className="h-6 w-6 text-emerald-500" />
          </div>
          <p className="text-lg font-medium text-slate-900">No discrepancies</p>
          <p className="text-slate-500 text-sm mt-1">All deliveries received without issues</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="space-y-3">
        {pendingCount > 0 && (
          <div className="p-3 rounded-lg bg-blue-50 border border-blue-100 mb-4">
            <p className="text-sm font-medium text-blue-800">
              {pendingCount} {pluralize(pendingCount, 'resolution', 'resolutions')} awaiting your confirmation
            </p>
          </div>
        )}
        {discrepancies.map((discrepancy) => (
          <DiscrepancyListItem
            key={discrepancy.discrepancyId}
            discrepancy={discrepancy}
            isSelected={selectedDiscrepancy?.discrepancyId === discrepancy.discrepancyId}
            onSelect={() => onSelectDiscrepancy(discrepancy)}
          />
        ))}
      </div>

      <div className="lg:sticky lg:top-6">
        {selectedDiscrepancy ? (
          <ResolutionRoom discrepancy={selectedDiscrepancy} />
        ) : (
          <Card className="border-slate-200 h-[600px] flex items-center justify-center">
            <div className="text-center">
              <FileText className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Select an item to view details</p>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

interface DiscrepancyListItemProps {
  readonly discrepancy: Discrepancy;
  readonly isSelected: boolean;
  readonly onSelect: () => void;
}

function DiscrepancyListItem({
  discrepancy, isSelected, onSelect 
}: DiscrepancyListItemProps) {
  const needsConfirmation = discrepancy.status === 'pending_receiver_confirmation';
  const isResolved = discrepancy.status === 'resolved';

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });

  const getButtonClassName = () => {
    if (isSelected) {
      return 'border-slate-900 bg-slate-50 ring-1 ring-slate-900';
    }
    if (needsConfirmation) {
      return 'border-blue-200 bg-blue-50/30 hover:border-slate-300';
    }
    return 'border-slate-200 bg-white hover:border-slate-300';
  };

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full text-left p-4 rounded-lg border transition-all ${getButtonClassName()}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium text-slate-900">{DISCREPANCY_TYPE_LABELS[discrepancy.type]}</span>
            {needsConfirmation && <span className="text-xs text-blue-700 font-medium">Review proposal</span>}
            {isResolved && (
              <span className="flex items-center gap-1 text-xs text-emerald-600">
                <CheckCircle className="h-3 w-3" />
                Resolved
              </span>
            )}
          </div>
          <p className="text-sm text-slate-500 truncate">
            {discrepancy.poNumber} • {discrepancy.supplierName}
          </p>
          <p className="text-xs text-slate-400 mt-1">Reported {formatDate(discrepancy.reportedAt)}</p>
        </div>
        <ChevronRight className={`h-5 w-5 flex-shrink-0 transition-colors ${isSelected ? 'text-slate-900' : 'text-slate-300'}`} />
      </div>
    </button>
  );
}
